import React, { useState, useEffect, useRef } from 'react';
import { getFromCache, preloadAndCache } from '../utils/globalImageCache';
import { isReturningToHome } from '../utils/imageTransitionUtils';

// This component handles image caching and loading using our enhanced caching system
const CachedImage = ({ 
  src, 
  alt, 
  style = {}, 
  className = '', 
  fallbackSrc,
  elementId,
  asBackground = false,
  backgroundSize = 'cover',
  backgroundPosition = 'center',
  transitionDuration = 0.3,
  onClick,
  children
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  
  useEffect(() => {
    let isActive = true;
    
    if (!src) return;

    const returning = isReturningToHome();
    
    if (!returning) {
      setLoaded(false);
    }
    
    const loadImage = async () => {
      try {
        const cachedImage = await getFromCache(src);
        
        if (!isActive) return;

        if (cachedImage && cachedImage.base64) {
          setImageSrc(cachedImage.base64);
          setLoaded(true);
          return;
        }

        const loadedImage = await preloadAndCache(src);
        
        if (!isActive) return;

        if (loadedImage && loadedImage.base64) {
          setImageSrc(loadedImage.base64);
        } else {
          setImageSrc(src);
        }
        
        if (returning) {
          setLoaded(true);
        } else {
          setTimeout(() => {
            if (isActive) setLoaded(true);
          }, 50);
        }

      } catch (error) {
        console.warn(`Failed to load image: ${src}`, error);
        if (!isActive) return;
        
        if (fallbackSrc) {
          setImageSrc(fallbackSrc);
          preloadAndCache(fallbackSrc).catch(e => console.error("Fallback cache failed", e));
        } else {
           setImageSrc(src);
        }
        setLoaded(true);
      }
    };

    loadImage();

    return () => {
      isActive = false;
    };
  }, [src, fallbackSrc]);

  const commonStyle = {
    ...style,
    transition: `opacity ${transitionDuration}s ease-in-out, filter ${transitionDuration}s ease-in-out`,
    opacity: loaded ? 1 : 0,
  };

  if (asBackground) {
    return (
      <div
        id={elementId}
        ref={imgRef}
        className={`${className} ${loaded ? 'loaded' : ''}`}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        aria-label={alt}
        style={{
          ...style,
          backgroundImage: loaded ? `url(${imageSrc})` : 'none',
          backgroundSize: backgroundSize,
          backgroundPosition: backgroundPosition,
          backgroundRepeat: 'no-repeat',
          opacity: loaded ? 1 : 0, 
          transition: `opacity ${transitionDuration}s ease-in-out`
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <img
      id={elementId}
      ref={imgRef}
      src={imageSrc}
      alt={alt || "Image"}
      loading="lazy"
      className={className}
      onClick={onClick}
      style={commonStyle}
      onLoad={() => {
          if (!loaded && imageSrc === src) {
              setLoaded(true);
          }
      }}
      onError={(e) => {
        console.warn("Image error in rendered component:", e.target.src);
        if (fallbackSrc && imageSrc !== fallbackSrc) {
          setImageSrc(fallbackSrc);
        }
      }}
    />
  );
};

export default CachedImage;
